import { Injectable } from '@angular/core';
import { Helper, Tool } from '@app/shared/utils';
import { ShareRecommendationFacade } from '@app/store/share-recommendation';
import { filter, take, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { GTMService } from '../tracking';
import { startConsultationGtm } from '@app/core/services/tracking';
import { AppRoutes } from '@app/shared/utils/enums/routes.enum';
import { ProductsFacade } from '@app/store/products/products.facade';
import { AllowanceFacade } from '@app/store/allowance';
import { PatientProfileFacade } from '@app/store/patient-profile';
import { ConsultationFacade } from '@app/store/consultation/consultation.facade';
import { CoreFacade } from '@app/store/core/core.facade';
import { PetFacade } from '@app/store/pet/pet.facade';
import { DailyAllowanceFacade } from '@app/pages/daily-allowance/store/daily-allowance.facade';
import { NewPetFacade } from '@app/pages/new-pet/store/new-pet.facade';
import { RenalDetectFacade } from '@app/pages/renal-detect/store/renal-detect.facade';
import { SmartRecoFacade } from '@app/pages/smart-reco/store/smart-reco.facade';
import { WeightManagementFacade } from '@app/pages/weight-management/weight-management.facade';

@Injectable({ providedIn: 'root' })
export class StartFlowService {
  constructor(
    private readonly router: Router,
    private readonly trackingService: GTMService,
    private readonly productsFacade: ProductsFacade,
    private readonly allowanceFacade: AllowanceFacade,
    private readonly consultationFacade: ConsultationFacade,
    private readonly coreFacade: CoreFacade,
    private readonly patientProfileFacade: PatientProfileFacade,
    private readonly petFacade: PetFacade,
    private readonly shareRecommendationFacade: ShareRecommendationFacade,
    private readonly dailyAllowanceFacade: DailyAllowanceFacade,
    private readonly newPetFacade: NewPetFacade,
    private readonly renalDetectFacade: RenalDetectFacade,
    private readonly smartRecoFacade: SmartRecoFacade,
    private readonly weightManagementFacade: WeightManagementFacade,
  ) {}

  public startFlowNewPatient(tool: Tool): void {
    switch (tool) {
      case Tool.SmartReco:
        this.prepareDataNewPatient(Tool.SmartReco);
        this.smartRecoFacade.startSmartReco(true);
        break;

      case Tool.RapidReco:
        this.prepareDataNewPatient(Tool.RapidReco);
        this.patientProfileFacade.resetPatientProfileState();
        this.coreFacade.toolInfo$
          .pipe(
            take(1),
            filter(({ prevTool }) => prevTool !== Tool.RapidReco),
            tap(() => this.productsFacade.resetProductsState()),
          )
          .subscribe();
        this.router.navigate([AppRoutes.RAPID_RECO, AppRoutes.PRODUCTS]);
        break;

      case Tool.RenalDetect:
        this.prepareDataNewPatient(Tool.RenalDetect);
        this.renalDetectFacade.startRenalDetect();
        break;

      case Tool.Rationing:
        this.smartRecoFacade.resetSmartRecoState();
        this.weightManagementFacade.resetWeightManagementState();
        this.newPetFacade.resetNewPetState();
        this.patientProfileFacade.resetPatientProfileState();
        this.shareRecommendationFacade.resetRecommendationRandomIds();
        this.petFacade.resetGrowthChart();
        this.coreFacade.setTool(Tool.Rationing, Helper.randomId());
        this.trackingService.sendInteraction(startConsultationGtm(Tool.Rationing));
        this.router.navigate([AppRoutes.PRODUCTS, AppRoutes.DAILY_ALLOWANCE]);
        break;

      case Tool.WeightManagement:
        this.prepareDataNewPatient(Tool.WeightManagement);
        this.weightManagementFacade.startWeightManagement();
        break;

      default:
        break;
    }
  }

  // in this use case, we dont reset consultation or products data
  // indeed, products are already selected and stored in the consultation store
  // consultation and products state were reset upon product book page setup (ngOnInit)
  public startNewDAFromProductBook(): void {
    this.trackingService.sendInteraction(startConsultationGtm(Tool.Rationing));
    this.shareRecommendationFacade.resetShareRecommendation();
    this.petFacade.resetGrowthChart();
    this.shareRecommendationFacade.resetRecommendationRandomIds();
    this.coreFacade.setTool(Tool.Rationing, Helper.randomId());
    this.consultationFacade.setConsultationSetSkipProductBook(true);
    this.dailyAllowanceFacade.startDailyAllowance(true);
  }

  /**
   *  we dont setTool and send startConsultationGtm because we have aleardy done it in startFlowNewPatient()
   *  we dont reset consultation or products data
   *  indeed, products are already selected and stored in the consultation store
   */
  public continueDAFromProductBook(): void {
    this.shareRecommendationFacade.resetShareRecommendation();
    this.petFacade.resetGrowthChart();
    this.consultationFacade.setConsultationSetSkipProductBook(true);
    this.dailyAllowanceFacade.startDailyAllowance(true);
  }

  private prepareDataNewPatient(tool: Tool) {
    this.trackingService.sendInteraction(startConsultationGtm(tool));
    this.petFacade.resetGrowthChart();
    this.consultationFacade.resetConsultationData();
    this.allowanceFacade.resetAllowanceParams();
    this.productsFacade.resetProductsState();
    this.shareRecommendationFacade.resetShareRecommendation();
    this.shareRecommendationFacade.resetRecommendationRandomIds();
    this.coreFacade.setTool(tool, Helper.randomId());
  }
}
